/**
 * Which of the drawn glyphs actually get painted.
 *
 * The decision is made here, on screen geometry, rather than left to MapLibre's own placement: that
 * drops a box that *intersects* another, and the reader's complaint is a pile whose boxes are
 * merely close. A crowded glyph is neither moved nor faked — it is simply not drawn this frame, and
 * the next tick, or the next zoom step, re-decides. Position on the map stays exactly where the
 * store put it, which is the one thing a decoration must not invent.
 *
 * The module knows nothing about aircraft or airports: it ranks anything that carries a point and a
 * key, so the fleet and the aerodromes are ranked by one rule instead of two.
 */

/** A point in the map's own frame: what a projection answers, and the units a clearance is in. */
export type ScreenPoint = { x: number; y: number };

/** Everything the declutter can rank: any feature that carries a point and a key. */
export type PlacedLike = {
  geometry: { coordinates: [number, number] };
  properties: { key: string };
};

/**
 * The features that are drawn: the ones the ranking wants first, each kept only when its projected
 * centre is at least `clearance` pixels from every glyph already kept.
 */
export const declutter = <Feature extends PlacedLike>(
  features: readonly Feature[],
  project: (coordinates: [number, number]) => ScreenPoint,
  clearance: number,
  compare: (a: Feature, b: Feature) => number,
): Feature[] => {
  const kept: Feature[] = [];
  const keptAt: ScreenPoint[] = [];
  for (const feature of [...features].sort(compare)) {
    const at = project(feature.geometry.coordinates);
    const clear = keptAt.every((other) => Math.hypot(other.x - at.x, other.y - at.y) >= clearance);
    if (!clear) continue;
    kept.push(feature);
    keptAt.push(at);
  }
  return kept;
};

/** A code-unit comparison, so a tie-break can never depend on the reader's locale. */
export const byKey = (a: PlacedLike, b: PlacedLike): number =>
  Number(a.properties.key > b.properties.key) - Number(a.properties.key < b.properties.key);
